/**
 * Auth state derived from MSAL.
 *
 * Replaces the old SWA `/.auth/me` lookup. The signed-in account comes
 * from the MSAL cache; roles (if any) are read from the ID token's
 * `roles` claim. `useMe` keeps the same shape the UI already consumes
 * ({ data, isLoading }).
 */

import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../auth/msalConfig';

export interface UserInfo {
  userId: string;
  userDetails: string;
  userRoles: string[];
}

export function useMe(): { data: UserInfo | null; isLoading: boolean } {
  const { instance, accounts, inProgress } = useMsal();
  // Prefer the ACTIVE account so nav role checks agree with the router's
  // /admin beforeLoad gate (which reads getActiveAccount()).
  const account = instance.getActiveAccount() ?? accounts[0];

  if (!account) {
    // `inProgress` is 'startup' until the first interaction settles.
    return { data: null, isLoading: inProgress !== 'none' };
  }

  const claims = account.idTokenClaims as { roles?: string[] } | undefined;
  return {
    data: {
      userId: account.localAccountId,
      userDetails: account.username || account.name || account.localAccountId,
      userRoles: ['authenticated', ...(claims?.roles ?? [])],
    },
    isLoading: false,
  };
}

/** Returns a handler that starts an interactive login redirect. */
export function useLogin(): () => void {
  const { instance } = useMsal();
  return () => {
    void instance.loginRedirect(loginRequest);
  };
}

/** Returns a handler that logs out + redirects to the post-logout URI. */
export function useLogout(): () => void {
  const { instance } = useMsal();
  return () => {
    void instance.logoutRedirect();
  };
}
